import { computed, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useRefreshPulse } from '@/composables/useRefreshPulse'
import { notifySuccess } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/error-message'
import { trackMixpanel } from '@/services/mix-panel'
import notificationPreferencesService from '@/services/notification-preferences.service'

/**
 * THE app-wide vote poller. One timer, one fetch loop, one place that decides a
 * vote landed.
 *
 * This used to live inside OrdersTable, which meant live progress only existed
 * while the user happened to be looking at `/#/orders`. Buy votes, open the
 * websites page to pick the next toplist, and the panel went silent — the votes
 * you had just paid for arrived with no sign of it. The poll is the product's
 * heartbeat, so it belongs above the router, not inside one screen.
 *
 * ── EXACTLY ONE POLLER ────────────────────────────────────────────────────────
 * The controller is a module singleton created on the first call and handed back
 * to every caller after that, so App.vue and OrdersTable share one timer, one
 * in-flight request and one set of "last updated" numbers. Two pollers would not
 * merely double the request rate: both would be diffing vote counts off the same
 * store, and each one's fetch would move the data the other was about to measure
 * against, so the toasts would double-count some polls and lose others entirely.
 *
 * ── WHY THE BASELINE IS OURS AND NOT THE STORE'S ──────────────────────────────
 * The old diff read `orders.previousOrdersAlert`, a snapshot the `listOrders`
 * ACTION takes of the current list on every dispatch. That snapshot is shared
 * mutable state and `listOrders` is dispatched from a dozen places — pause,
 * resume, hide, unhide, the edit modal, the bulk buttons — none of which are
 * coordinated with the poll. Interleave two of them and the baseline you are
 * about to diff against has already been replaced by somebody else's, which
 * silently double-counts a delta (announce A→B, then announce A→C) or drops one.
 *
 * So the baseline here is a private snapshot of the LAST ANNOUNCED RESULT, held
 * in this module and advanced only when a poll of ours settles. Every fetch
 * diffs its own payload against its own baseline; nothing outside can move it.
 * Consequences worth stating:
 *   • the windows chain exactly — one poll's baseline is the previous poll's
 *     result — so no vote is counted twice and none falls between two polls;
 *   • a `listOrders` fired by some other component in between is simply absorbed
 *     into the next window, which is the honest outcome: the votes are still
 *     counted, just attributed to the poll that observed them;
 *   • overlapping fetches cannot double-count, because a refresh already in
 *     flight is COALESCED (below) instead of starting a second window.
 *
 * `previousOrdersAlert` is deliberately left in the store untouched: it is no
 * longer read by anything, and ripping it out is a separate change to an action
 * that a dozen call sites dispatch.
 *
 * ── HIDDEN TABS DO NOT POLL ───────────────────────────────────────────────────
 * See `onVisibilityChange`. A backgrounded tab is the expensive case — a panel
 * left open overnight is otherwise 800 requests nobody will ever see the result
 * of — so `document.hidden` STOPS the cycle (clears the timer) rather than
 * skipping a fetch and rescheduling.
 *
 * `visibilitychange` is the only signal used. `window` blur/focus was considered
 * and rejected: those fire when the window merely loses keyboard focus while
 * still fully on screen — a second monitor, devtools, an iframe taking focus —
 * so blur would freeze a table the user is still watching, and focus would fire
 * a catch-up fetch on every alt-tab back to a tab that never stopped polling.
 * `visibilitychange` covers the cases that actually matter (other tab, minimised
 * window, phone screen off) and nothing else.
 */

/* On the jobs table the numbers are the content of the screen, so it keeps the
   cadence it has always had. */
const ORDERS_POLL_INTERVAL_MS = 30_000

/* Everywhere else the poll only exists to fire toasts, and 105s is about as
   slow as it can be while a "+N votes" message still feels like news. Slower
   than 2 minutes and the toast reads as a summary of something that already
   finished happening. */
const BACKGROUND_POLL_INTERVAL_MS = 105_000

/* `/` and `/orders` both render views/Orders.vue — the same screen, so the same
   cadence. Matched on the normalised path rather than the route name because `/`
   is named "home" and carries no clue that it is the jobs table. */
const ORDERS_PATHS = new Set(['/', '/campaigns'])

/** The one controller. Rebuilt only if whoever created it is torn down. */
let controller = null

/**
 * One poll cycle's worth of jobs, reduced to what the diff needs.
 * @returns {Map<number, {website: string, value: number}>|null} null if the
 *          payload was not a list — an unusable answer must not be mistaken for
 *          "every job vanished".
 */
const snapshot = (orders) => {
  if (!Array.isArray(orders)) return null

  const byId = new Map()
  orders.forEach((order) => {
    if (!order || order.id === undefined || order.id === null) return
    byId.set(order.id, {
      website: order?.website?.name ?? 'your site',
      value: Number(order?.amount_done) || 0,
    })
  })
  return byId
}

/**
 * Votes gained between two snapshots, collected per SITE rather than per job —
 * several jobs can run against the same toplist and their counts belong on one
 * line.
 *
 * Jobs missing from the baseline are skipped: a job created since the last poll
 * arrives with its whole `amount_done` already on it, and announcing that as
 * votes that just landed would be a lie the size of the job.
 *
 * The old implementation carried `seen` flags on both sides to stop the same
 * pair being announced twice. They are gone with the shared snapshot: each
 * result is diffed exactly once, by the fetch that produced it.
 */
const collectVotes = (before, after) => {
  const votesBySite = new Map()
  if (!before || !after) return votesBySite

  after.forEach((current, id) => {
    const previous = before.get(id)
    if (!previous) return

    const gained = current.value - previous.value
    if (gained > 0) {
      votesBySite.set(current.website, (votesBySite.get(current.website) ?? 0) + gained)
    }
  })

  return votesBySite
}

/** Busiest sites first. Shared by the toast and the chip below. */
const rankSites = (votesBySite) => [...votesBySite.entries()].sort((a, b) => b[1] - a[1])

/** "A +7 · B +5 · C +4 · +2 more sites" — the three-site cap, in one place. */
const listSites = (sites) => {
  const shown = sites.slice(0, 3).map(([name, count]) => `${name} +${count}`).join(' · ')
  const rest = sites.length - 3
  return rest > 0 ? `${shown} · +${rest} more site${rest === 1 ? '' : 's'}` : shown
}

/*
 * One poll can advance many jobs at once, and several jobs often share a site.
 * Firing a toast per job stacked 19 of them over the table. This announces the
 * whole batch once: total first, then the busiest sites.
 *
 * Moved here verbatim from OrdersTable — the wording, the three-site cap and the
 * dedupe key are the behaviour, not decoration.
 *
 * The escapeHtml helper that used to guard this is gone with it: the old
 * library rendered the body as raw HTML, so API-supplied site names had to be
 * escaped by hand. ToastItem interpolates text, so that whole class of bug is
 * no longer reachable.
 */
const announceVotes = (votesBySite) => {
  const sites = rankSites(votesBySite)
  const total = sites.reduce((sum, [, count]) => sum + count, 0)
  if (total <= 0) return

  const detail = sites.length === 1 ? `on ${sites[0][0]}` : listSites(sites)

  notifySuccess(`+${total} vote${total === 1 ? '' : 's'}`, {
    detail,
    duration: 4000,
    // Every poll produces a different total, so the default wording-based key
    // would never collapse. Keying on the batch itself means a poll that fires
    // twice before the first toast clears bumps a count instead of stacking.
    dedupeKey: `votes:${detail}`
  })
}

/*
 * The same batch for the chip on /campaigns: "+18 votes · last 30s", with the
 * busiest sites kept for its tooltip. `windowMs` is the time since the previous
 * poll, i.e. how long those votes took to arrive.
 */
const summariseVotes = (votesBySite, windowMs) => {
  const sites = rankSites(votesBySite)
  const total = sites.reduce((sum, [, count]) => sum + count, 0)
  if (total <= 0) return null

  const seconds = Math.max(1, Math.round(windowMs / 1000))
  const span = seconds < 90 ? `${seconds}s` : `${Math.round(seconds / 60)}m`
  return {
    total,
    label: `+${total} vote${total === 1 ? '' : 's'} · last ${span}`,
    title: listSites(sites),
  }
}

function createController() {
  const store = useStore()
  const route = useRoute()

  const isLoggedIn = computed(() => store.state.auth.status.loggedIn)

  const isOrdersRoute = computed(() => {
    const path = route.path || '/'
    const normalised = path.length > 1 ? path.replace(/\/+$/, '') || '/' : path
    return ORDERS_PATHS.has(normalised)
  })

  const pollIntervalMs = computed(() =>
    isOrdersRoute.value ? ORDERS_POLL_INTERVAL_MS : BACKGROUND_POLL_INTERVAL_MS
  )

  /* The cadence is passed as the computed itself, not as a number: the ring and
     the "next refresh in Ns" label have to follow a route change without this
     composable being rebuilt, which would reset the "Updated Ns ago" clock. */
  const pulse = useRefreshPulse({ pollIntervalMs })

  /** Set by a failed poll, cleared by a successful one. Rendered by the table. */
  const error = ref(null)

  /*
   * Whether the "+N votes" toasts are wanted. Defaults to ON and stays ON if the
   * lookup fails — the endpoint may not exist yet, and a preference we could not
   * read must not be treated as "off": that is indistinguishable, from the
   * user's side, from the feature being broken.
   *
   * Gates the TOASTS only. Polling and the orders data itself continue whatever
   * this says, because the jobs page renders from that data — turning off a
   * notification must never turn off the screen it notifies about.
   */
  const voteToastsEnabled = ref(true)
  let preferenceLoaded = false

  /*
   * The last poll's gains while the campaigns table is open, for the passive
   * chip beside "Updated Ns ago" — or null when that poll brought nothing.
   *
   * On the table itself a toast is the wrong tool: the rows that moved already
   * flash, and a card over the top right of the screen every 30s covers the
   * thing it is announcing. So on that route the batch lands here instead and
   * no toast fires at all. Everywhere else the toasts, and the vote_toasts
   * preference that gates them, behave exactly as before.
   */
  const recentVotes = ref(null)

  /* The chip only means something while you are looking at the table. */
  watch(isOrdersRoute, (onTable) => {
    if (!onTable) recentVotes.value = null
  })

  const loadPreference = () => {
    if (preferenceLoaded) return
    preferenceLoaded = true

    notificationPreferencesService
      .get()
      .then(({ data }) => {
        // `available: false` means the backend has no column to answer from —
        // an absent answer, not a "no". Same handling as an outright failure.
        if (!data || data.available === false) return
        voteToastsEnabled.value = data.vote_toasts !== false
      })
      .catch(() => {
        // Deliberately silent: a 404 here is the expected state until the
        // endpoint ships, and an error toast about the toast preference would be
        // both useless and self-parodying.
      })
  }

  /* ── Scheduling ─────────────────────────────────────────────────────────────
     `pollTimeout` is the ONLY timer. Every path that schedules goes through
     `schedule()`, which clears it first, so at any instant there is exactly one
     pending poll or none — never two racing at different cadences. */
  let pollTimeout = null

  /** When the current cycle was armed, for re-arming across a cadence change. */
  let cycleStartedAt = null

  /** The snapshot the next result is measured against. See the header. */
  let baseline = null

  /** When `baseline` was taken, so the chip can say how long its votes took. */
  let baselineAt = 0

  /** The poll in the air, if any. Shared so a second caller joins it. */
  let inFlight = null

  const isActive = () => isLoggedIn.value && !document.hidden

  const schedule = (delayMs) => {
    clearTimeout(pollTimeout)
    pollTimeout = setTimeout(() => {
      pollTimeout = null
      refresh('auto')
    }, Math.max(0, delayMs))
  }

  /** Stop entirely: no timer, and a blanked ring rather than one counting down
      to a refresh that is not coming. */
  const suspend = () => {
    clearTimeout(pollTimeout)
    pollTimeout = null
    cycleStartedAt = null
    pulse.stopCycle()
  }

  /** Start the clock again from now. Called after every completed refresh. */
  const armPoll = () => {
    if (!isActive()) {
      suspend()
      return
    }

    cycleStartedAt = Date.now()
    pulse.startCycle()
    schedule(pollIntervalMs.value)
  }

  /**
   * Fetch the jobs, announce whatever votes landed, and re-arm.
   *
   * @param {'auto'|'manual'|'visible'|'route'|'start'} source for telemetry.
   */
  const refresh = (source = 'auto') => {
    if (!isLoggedIn.value) return Promise.resolve()

    /* Every refresh — automatic, manual, or on tab focus — restarts the cycle,
       so the next automatic one is always a full interval away from the last
       actual fetch. This is what the ring draws. */
    armPoll()

    /*
     * Mixpanel only hears about polls that would have existed before this
     * change: the jobs page, and any manual press. The app-wide poll runs in
     * every logged-in tab forever, and reporting each of those would multiply
     * the event volume by the number of pages nobody is looking at.
     */
    if (source === 'manual') {
      trackMixpanel('refreshOrders', { source: 'manual' })
    } else if (isOrdersRoute.value) {
      trackMixpanel('refreshOrders', {})
    }

    /*
     * COALESCED, not queued. A manual press landing on top of an automatic poll
     * used to mean two fetches measuring overlapping windows, and the second one
     * would announce votes the first had already announced. Joining the request
     * already in the air gives the presser exactly what they asked for — the
     * freshest data — with one window and one toast.
     */
    if (inFlight) return inFlight

    const before = baseline

    const request = store
      .dispatch('orders/listOrders')
      .then((orders) => {
        error.value = null
        // Success only — a failed poll must let the label keep ageing so it can
        // go stale rather than claim data it never received.
        pulse.markUpdated()

        const after = snapshot(orders)
        if (!after) return orders

        /*
         * No baseline means this is the first poll of the session, and there is
         * nothing to compare against. Seeding the baseline from the localStorage
         * cache (which is what the old store-snapshot version effectively did)
         * was rejected: it turns every page load into a toast for every vote
         * gained since the tab was last open — "+4130 votes" on arrival — and
         * now that the poller runs app-wide, that would greet the user on
         * whatever page they happened to open.
         */
        if (before) {
          const votesBySite = collectVotes(before, after)
          if (isOrdersRoute.value) {
            // A quiet poll clears the chip rather than leaving old news up.
            recentVotes.value = summariseVotes(votesBySite, Date.now() - baselineAt)
          } else {
            recentVotes.value = null
            if (voteToastsEnabled.value && votesBySite.size > 0) announceVotes(votesBySite)
          }
        }

        baseline = after
        baselineAt = Date.now()
        return orders
      })
      .catch((err) => {
        error.value = getErrorMessage(
          err,
          'We could not load your campaigns right now. Please try again in a moment.'
        )
      })
      .finally(() => {
        inFlight = null
      })

    inFlight = pulse.track(request)
    return inFlight
  }

  /**
   * A hidden tab STOPS. Not "skips this one and reschedules" — the timer is
   * cleared, so a tab left in the background costs nothing at all until it comes
   * back.
   *
   * Coming back fetches once immediately rather than waiting out a fresh
   * interval: the figures on screen are by definition as old as the time away,
   * and `refresh` re-arms the cycle from that fetch, so the cadence resumes
   * normally from a known-good starting point.
   *
   * A fetch already in the air when the tab was hidden is NOT cancelled — axios
   * would need an abort controller threaded through the store action for that,
   * and the request is already paid for. It settles into the baseline as usual;
   * the only visible effect is that its toast fires against a tab nobody is
   * looking at, which the toast host holds until they come back.
   */
  const onVisibilityChange = () => {
    if (!isLoggedIn.value) return

    if (document.hidden) {
      suspend()
      return
    }

    refresh('visible')
  }

  /*
   * A route change re-arms at the new cadence instead of waiting out the old
   * one, WITHOUT creating a second timer — `schedule` clears before it sets.
   *
   * The elapsed part of the current cycle is carried over rather than restarted,
   * so leaving the jobs page cannot postpone a poll indefinitely by navigating
   * repeatedly, and arriving on it does not silently discard 20s of waiting.
   * When more time has already passed than the new cadence allows — 40s into a
   * 105s cycle, then landing on the jobs page, which wants 30s — the poll is
   * simply overdue and fires now. That is also what makes the table fresh on
   * arrival without OrdersTable needing a fetch of its own.
   *
   * Watching the CADENCE rather than the route means navigating between two
   * background pages does not touch the timer at all.
   */
  watch(pollIntervalMs, (interval) => {
    if (!isActive() || cycleStartedAt === null) return

    const elapsed = Date.now() - cycleStartedAt
    if (elapsed >= interval) {
      refresh('route')
      return
    }

    // `cycleStartedAt` is deliberately left alone: the ring reads the same start
    // against the new interval, so it jumps to the correct fraction rather than
    // resetting to full and lying about how long is left.
    schedule(interval - elapsed)
  })

  watch(
    isLoggedIn,
    (loggedIn) => {
      if (!loggedIn) {
        suspend()
        // The next account must not inherit this one's vote counts.
        baseline = null
        recentVotes.value = null
        preferenceLoaded = false
        voteToastsEnabled.value = true
        return
      }

      loadPreference()

      /* A tab that loads in the background — restored session, opened in a new
         tab, prerender — arms nothing and fetches nothing. `visibilitychange`
         starts it the moment it is actually looked at. */
      if (document.hidden) {
        suspend()
        return
      }

      refresh('start')
    },
    { immediate: true }
  )

  document.addEventListener('visibilitychange', onVisibilityChange, false)

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange, false)
    suspend()
    inFlight = null
    baseline = null
    // Whoever created the singleton is going away, so the next caller has to
    // build a fresh one rather than adopt a controller with no timer and a dead
    // clock. In practice this only fires when the whole app unmounts.
    controller = null
  })

  return {
    /* Poll state, rendered by the jobs table. */
    refreshing: pulse.refreshing,
    lastUpdatedAt: pulse.lastUpdatedAt,
    lastUpdatedLabel: pulse.lastUpdatedLabel,
    lastUpdatedTitle: pulse.lastUpdatedTitle,
    isStale: pulse.isStale,
    nextRefreshFraction: pulse.nextRefreshFraction,
    secondsToNextRefresh: pulse.secondsToNextRefresh,
    error,

    /* `{ total, label, title }` or null — the chip on the campaigns table. */
    recentVotes,

    /* Writable on purpose. The settings screen owns POSTing the preference; it
       can assign here too, so a toggle takes effect on the running poller
       instead of on the next page load. */
    voteToastsEnabled,

    /** Manual refresh. Same code path, same bookkeeping, as the automatic one. */
    refresh,
  }
}

/**
 * The app-wide vote poller.
 *
 * Call it once from App.vue to START it — that call decides the poller's
 * lifetime, which is why it belongs at the root and not in a screen. Every later
 * call (OrdersTable) gets the same controller back and is a pure consumer: it
 * reads the ring, the "Updated Ns ago" label and the in-flight flag, and can ask
 * for a manual refresh.
 */
export function useVoteActivity() {
  if (!controller) controller = createController()
  return controller
}

export default useVoteActivity
